/**
 * Rate limit configuration for all endpoints
 *
 * Multi-tier rate limiting:
 * - Layer 1: Cloudflare native (IP-based, fast, at edge)
 * - Layer 2: Redis (user-based, granular, for authenticated endpoints)
 */

/**
 * Rate limit tiers using Cloudflare bindings
 */
export const RATE_LIMIT_TIERS = {
  /** Very restrictive - OAuth, auth endpoints (5/min) */
  STRICT: { limit: 5, period: 60, bindingName: 'RATE_LIMITER_STRICT' },
  /** Moderate - billing, token exchange (30/min) */
  MODERATE: { limit: 30, period: 60, bindingName: 'RATE_LIMITER_MODERATE' },
  /** Standard - general API endpoints (100/min) */
  STANDARD: { limit: 100, period: 60, bindingName: 'RATE_LIMITER_STANDARD' },
  /** High throughput - message webhooks (300/min) */
  HIGH: { limit: 300, period: 60, bindingName: 'RATE_LIMITER_HIGH' },
} as const;

export type RateLimitTier = keyof typeof RATE_LIMIT_TIERS;

/**
 * Configuration for a specific endpoint's rate limiting
 */
export interface EndpointRateLimitConfig {
  /** IP-based tier using Cloudflare binding */
  tier: RateLimitTier;
  /** User-based limit (requests per minute) - only for authenticated requests */
  userLimit?: number;
  /** Window in seconds for user limit (default: 60) */
  userWindow?: number;
  /** If true, return 503 when Redis is unavailable (default: false = fail-open) */
  failClosed?: boolean;
  /** If true, skip IP rate limit for authenticated users (default: false) */
  skipAuthenticatedUsers?: boolean;
}

/**
 * Endpoint configurations
 * Keys are path patterns that support wildcards (*)
 */
const ENDPOINT_CONFIGS: Record<string, EndpointRateLimitConfig> = {
  // OAuth callbacks - strict, fail-closed (prevent brute force)
  '/oauth/callback/*': { tier: 'STRICT', failClosed: true },

  // Auth endpoints - strict, fail-closed
  '/auth/phone': { tier: 'STRICT', failClosed: true },
  '/auth/mcp/exchange': { tier: 'MODERATE', failClosed: true },
  // Native magic-code login (pre-auth) — STRICT per-IP bounds a single host; WorkOS owns
  // the per-email lockout / TTL / single-use guarantees.
  '/api/auth/magic-code/*': { tier: 'STRICT', failClosed: true },

  // Billing - moderate, fail-closed with user limits
  '/api/billing/*': { tier: 'MODERATE', userLimit: 100, failClosed: true, skipAuthenticatedUsers: true },
  '/webhooks/stripe': { tier: 'MODERATE', failClosed: true },
  '/webhooks/workos': { tier: 'MODERATE', failClosed: true },

  // API endpoints - standard with user limits
  '/api/setup/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/conversations/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/users/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/analytics/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/evals/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/files/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/outbound/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/completions/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/live-updates/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/hub-users/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/conversation-state/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },

  // Channel webhooks - high throughput (external systems, never authenticated)
  '/webhooks/whatsapp/*': { tier: 'HIGH' },
  '/webhooks/instagram/*': { tier: 'HIGH' },

  // Reports (CLI → platform_report triage queue). One per-user budget covers intake
  // (POST), the content edit (PATCH /:id), the reporter read-back (GET, GET /:id), and the
  // verification loop (POST /:id/accept, /:id/contest). The limiter is path-based, so a
  // method-level split (loose reads, strict writes) isn't expressible — GET /:id and PATCH
  // /:id share a path. So the budget is sized for the read-heavy poll loop (a reporter agent
  // polling `report get`/`list` while it waits for the fix), at 60/hour. Intake-flooding stays
  // bounded regardless: identical reports collapse on `fingerprint` (ON CONFLICT DO NOTHING),
  // so a runaway create loop merges rather than floods. Still STRICT tier + failClosed so a
  // genuinely runaway distinct-report loop surfaces as 429. The `/*` is load-bearing — an
  // exact `/api/reports` key would leave the `/:id` paths unmatched (→ unthrottled).
  '/api/reports/*': { tier: 'STRICT', userLimit: 60, userWindow: 3600, failClosed: true, skipAuthenticatedUsers: true },

  // Admin report mutations (transition/group/ungroup/edit) — platform-admin
  // gated at the route, rate-limited here as defense-in-depth. Generous STANDARD
  // cap so legitimate triage bursts aren't throttled (grouping is one request
  // even for many members); deliberately NOT failClosed so a Redis blip never
  // blocks an admin's triage. The bare GET reads share this budget harmlessly.
  '/api/admin/reports/*': { tier: 'STANDARD', userLimit: 150 },

  // GitHub webhook (issues.closed → platform_report addressed). MODERATE +
  // fail-closed; GitHub retries on transient failures so a brief Redis blip
  // doesn't lose state.
  '/webhooks/github': { tier: 'MODERATE', failClosed: true },

  // Account deletion — strict on the destructive POST + public undo. The
  // /api/users/* default tier above ALSO matches these, but `getEndpointConfig`
  // returns the longest-prefix match, so the more specific entries here win.
  // `skipAuthenticatedUsers` deliberately omitted: every caller is authenticated,
  // so including it would make `userLimit` a no-op — the per-user cap IS the gate.
  '/api/users/me/deletion': { tier: 'STRICT', userLimit: 3, userWindow: 3600, failClosed: true },
  '/api/users/me/deletion/preflight': { tier: 'STANDARD', userLimit: 30, failClosed: true },
  '/api/users/deletion/undo': { tier: 'STRICT', failClosed: true },

  // Email-invite preview — public, fail-closed. MODERATE (30/min IP) leaves
  // headroom for the signup-page server-fetch + reloads + prefetches; STRICT
  // would throttle legitimate users opening the link in multiple tabs.
  '/api/invites/preview': { tier: 'MODERATE', failClosed: true },

  // Admin DO debug reads - very strict, fail-closed. Platform-admin only (enforced
  // at route), but we rate-limit independently as a defense-in-depth signal so a
  // runaway investigation script surfaces as 429 instead of silently scanning.
  '/api/admin/data-explorer/debug/*': { tier: 'STRICT', userLimit: 60, userWindow: 3600, failClosed: true },

  // Admin sandbox exec (harness-agents PR 1.5) — this is ARBITRARY CODE EXECUTION,
  // so it gets the strictest budget on the platform: STRICT tier + a low per-user
  // hourly cap (well below the 60/hr debug-read cap) + fail-closed. Platform-admin
  // is enforced at the route; this is the independent defense-in-depth signal so a
  // runaway exec loop surfaces as 429 instead of silently provisioning sandboxes.
  '/api/admin/sandbox/*': { tier: 'STRICT', userLimit: 20, userWindow: 3600, failClosed: true },

  // Admin harness incident-response (harness-agents PR 2.9b) — mass-destroy reaps
  // EVERY live harness sandbox/token/slot in scope, so it gets a strict budget
  // (STRICT + a low per-user hourly cap + fail-closed). Platform-admin is enforced
  // at the route; this is the independent defense-in-depth signal so a runaway
  // reap loop surfaces as 429. The break-glass disable/enable rides through
  // `/api/admin/config` (its own STANDARD budget), not this key.
  '/api/admin/harness/*': { tier: 'STRICT', userLimit: 10, userWindow: 3600, failClosed: true },

  // CI/GitOps endpoints - standard with user limits
  '/api/ci/*': { tier: 'STANDARD', userLimit: 100, skipAuthenticatedUsers: true },

  // App channel - high throughput with user limits
  '/channels/app/*': { tier: 'HIGH', userLimit: 500, skipAuthenticatedUsers: true },

  // System/scheduled - high throughput (internal)
  '/channels/system/*': { tier: 'HIGH', skipAuthenticatedUsers: true },
  '/scheduled/*': { tier: 'HIGH', skipAuthenticatedUsers: true },

  // WebSocket - standard with user limit to prevent connection spam
  '/live-updates/ws': { tier: 'STANDARD', userLimit: 100, failClosed: true },

  // WS ticket mint - per-user 60/min accommodates aggressive reconnect patterns
  // without opening abuse vectors; fail-closed so Redis outages don't let
  // runaway clients mint unbounded tickets.
  '/api/auth/ws-ticket': { tier: 'MODERATE', userLimit: 60, failClosed: true, skipAuthenticatedUsers: true },
  '/api/auth/session-check': { tier: 'STANDARD', userLimit: 120, skipAuthenticatedUsers: true },
  '/api/auth/logout': { tier: 'MODERATE', userLimit: 10, failClosed: true, skipAuthenticatedUsers: true },
};

/**
 * Paths that should be excluded from rate limiting entirely
 *
 * Note: /files/* is the R2 file serving route (public/signed URLs, no auth)
 * This is different from /api/files/* which is the authenticated API route
 */
const EXCLUDED_PATHS = new Set(['/health', '/files/*']);

/**
 * Check if a path matches a pattern
 * Supports wildcards (*) at the end of patterns
 *
 * Examples:
 * - '/api/billing/*' matches '/api/billing', '/api/billing/', and '/api/billing/foo'
 * - '/api/billing/*' does NOT match '/api/billing-new'
 */
function matchPath(pattern: string, path: string): boolean {
  // Normalize: remove trailing slash for consistent matching
  const normalizedPath = path.replace(/\/$/, '');

  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2); // Remove '/*'
    return normalizedPath === prefix || normalizedPath.startsWith(prefix + '/');
  }
  return pattern === normalizedPath;
}

/**
 * Get rate limit configuration for a specific path
 * Returns null if the path is excluded from rate limiting
 */
export function getEndpointConfig(path: string): EndpointRateLimitConfig | null {
  // Check exclusions first
  for (const excluded of EXCLUDED_PATHS) {
    if (matchPath(excluded, path)) {
      return null;
    }
  }

  // Find matching config (most specific first - longer patterns)
  const sortedPatterns = Object.keys(ENDPOINT_CONFIGS).sort((a, b) => b.length - a.length);

  for (const pattern of sortedPatterns) {
    if (matchPath(pattern, path)) {
      return ENDPOINT_CONFIGS[pattern];
    }
  }

  // Default config for unlisted endpoints
  return { tier: 'STANDARD' };
}

/**
 * Get the rate limit tier configuration
 */
export function getTierConfig(tier: RateLimitTier): (typeof RATE_LIMIT_TIERS)[RateLimitTier] {
  return RATE_LIMIT_TIERS[tier];
}
